import { configureStore } from '@reduxjs/toolkit';
import { createReduxEnhancer } from '@sentry/react';
import reduceReducers from 'reduce-reducers';

import rootReducer, { RootState } from 'reducers/root';
import { IntentWorkflowActions } from 'actions/workflows-v2/workflowsv2Types';
import {
  initIntentConversation,
  sendFreeFormQuery,
  updateIntentConversation,
} from 'actions/workflows-v2/workflowsV2';
import { WorkflowsActions } from 'actions/workflows/workflows';
import { configureAppActions } from 'actions/config/config';
import { persistMiddleware, getPreloadedState } from 'persistence/persistence';
import {
  enablePersistenceBetweenTabs,
  persistenceReducer,
} from 'persistence/persistenceBetweenTabs';
import lazyLogRocket from 'lazy-logrocket';

const sanitizeState = (state: RootState) => ({
  ...state,
  config: {
    ...state.config,
    apiKey: null,
    rawParams: null,
  },
  workflows: {
    ...state.workflows,
    freeFormQuery: null,
    widgetComponents: state.workflows?.widgetComponents.map(
      widgetComponent => ({
        ...widgetComponent,
        contextVariables: null,
        query: null,
      }),
    ),
  },
});

const LogRocketMiddleware = lazyLogRocket.reduxMiddleware({
  stateSanitizer: sanitizeState,
});

//NOTE if we add Middleware in the future, LogRocket should always be the last middleware added.
// https://docs.logrocket.com/reference#redux-logging
const store = configureStore({
  // https://redux-toolkit.js.org/api/getDefaultMiddleware
  middleware: getDefaultMiddleware =>
    getDefaultMiddleware().concat([persistMiddleware, LogRocketMiddleware]),

  /* eslint-disable-next-line sort-keys-fix/sort-keys-fix -- `middleware` MUST
  come before `enhancers`, see
  https://redux.js.org/usage/migrations/migrating-rtk-2#configurestore-field-order-for-middleware-matters */
  enhancers: getDefaultEnhancers =>
    getDefaultEnhancers().concat([
      createReduxEnhancer({
        actionTransformer: action => {
          if (
            action.type === IntentWorkflowActions.SET_FREE_FORM_QUERY ||
            action.type === configureAppActions.ADD_RAW_PARAMS ||
            action.type === configureAppActions.SET_API_KEY ||
            action.type === WorkflowsActions.SET_STEP_SENT ||
            action.type === WorkflowsActions.SET_STEP_CONTEXT_VARIABLES
          ) {
            return {
              ...action,
              payload: 'REDACTED',
            };
          }

          if (
            action.type === String(sendFreeFormQuery.pending) ||
            action.type === String(sendFreeFormQuery.rejected) ||
            action.type === String(sendFreeFormQuery.fulfilled) ||
            action.type === String(initIntentConversation.pending) ||
            action.type === String(initIntentConversation.rejected) ||
            action.type === String(initIntentConversation.fulfilled) ||
            action.type === String(updateIntentConversation.pending) ||
            action.type === String(updateIntentConversation.rejected) ||
            action.type === String(updateIntentConversation.fulfilled)
          ) {
            return {
              ...action,
              meta: {
                ...action.meta,
                arg: 'REDACTED',
              },
            };
          }

          return action;
        },
        attachReduxState: false,
      }),
    ]),

  preloadedState: getPreloadedState(),

  reducer: reduceReducers(rootReducer, persistenceReducer),
});

enablePersistenceBetweenTabs(store);

export default store;

export type AppDispatch = typeof store.dispatch;
